import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Copy, ExternalLink, Wallet } from "lucide-react";
import { api, money, resolveImg, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

function UsdcPaymentPanel({ order, onPaid }) {
  const [config, setConfig] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => { api.get("/payments/usdc/config").then(({ data }) => setConfig(data)).catch(() => {}); }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy — select and copy the ${label.toLowerCase()} manually`);
    }
  };

  const verifyHash = async (hash, silent = false) => {
    try {
      await api.post("/payments/usdc/verify", { order_id: order.order_id, tx_hash: hash });
      toast.success("Payment confirmed on Base!");
      onPaid();
      return true;
    } catch (err) {
      if (!silent) toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not verify payment");
      return false;
    }
  };

  const verify = async () => {
    if (!txHash.trim()) { toast.error("Paste your transaction hash first"); return; }
    setVerifying(true);
    await verifyHash(txHash.trim());
    setVerifying(false);
  };

  const payWithWallet = async () => {
    if (!window.ethereum) {
      toast.error("No crypto wallet detected. Install MetaMask or Coinbase Wallet, or send manually using the address below.");
      return;
    }
    setPaying(true);
    try {
      const [from] = await window.ethereum.request({ method: "eth_requestAccounts" });
      try {
        await window.ethereum.request({ method: "wallet_switchEthereumChain", params: [{ chainId: "0x2105" }] });
      } catch (switchErr) {
        if (switchErr.code === 4902) {
          await window.ethereum.request({ method: "wallet_addEthereumChain", params: [{
            chainId: "0x2105", chainName: "Base",
            nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
            rpcUrls: ["https://mainnet.base.org"], blockExplorerUrls: ["https://basescan.org"] }] });
        } else throw switchErr;
      }
      const units = BigInt(Math.round(order.total * 100)) * 10000n;
      const calldata = "0xa9059cbb" + config.wallet.slice(2).toLowerCase().padStart(64, "0") + units.toString(16).padStart(64, "0");
      const hash = await window.ethereum.request({
        method: "eth_sendTransaction",
        params: [{ from, to: config.usdc_contract, data: calldata }],
      });
      setTxHash(hash);
      toast.success("Transaction sent — waiting for confirmation…");
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        if (await verifyHash(hash, true)) { setPaying(false); return; }
      }
      toast.error("Still confirming — your hash is filled in below, tap Verify Payment in a moment.");
    } catch (err) {
      const msg = err?.code === 4001 ? "Payment cancelled in wallet" : (err?.message || "Wallet payment failed").slice(0, 120);
      toast.error(msg);
    } finally { setPaying(false); }
  };

  if (!config) return null;
  return (
    <div data-testid="usdc-payment-panel" className="border-2 border-[#4A5D23] bg-[#4A5D23]/5 p-6 mb-8">
      <h2 className="font-heading font-bold uppercase tracking-tight text-lg">Complete Your USDC Payment</h2>
      <p className="text-sm text-black/60 mt-1">Send exactly <strong className="text-black">{order.total.toFixed(2)} USDC</strong> on the <strong className="text-black">Base network</strong> to the wallet below, then paste your transaction hash to confirm.</p>
      <button data-testid="wallet-pay-btn" type="button" onClick={payWithWallet} disabled={paying || !config}
        className="mt-4 w-full bg-[#121212] text-white py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150 disabled:opacity-60 flex items-center justify-center gap-2">
        <Wallet size={16} /> {paying ? "Confirm in Your Wallet…" : `Pay ${order.total.toFixed(2)} USDC From Wallet`}
      </button>
      <p className="text-[11px] text-black/40 mt-2 text-center">One click with MetaMask, Coinbase Wallet or any browser wallet — or send manually below.</p>
      <div className="mt-4 space-y-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-black/50">Amount (USDC on Base)</p>
          <div className="flex items-center gap-2 mt-1">
            <code data-testid="usdc-amount" className="bg-white border border-black/15 px-3 py-2 text-sm font-bold flex-1">{order.total.toFixed(2)}</code>
            <button data-testid="copy-amount-btn" type="button" onClick={() => copy(order.total.toFixed(2), "Amount")} className="p-2.5 bg-[#121212] text-white hover:bg-[#4A5D23] transition-colors duration-150" aria-label="Copy amount"><Copy size={14} /></button>
          </div>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-black/50">Wallet Address</p>
          <div className="flex items-center gap-2 mt-1">
            <code data-testid="usdc-wallet" className="bg-white border border-black/15 px-3 py-2 text-xs break-all flex-1">{config.wallet}</code>
            <button data-testid="copy-wallet-btn" type="button" onClick={() => copy(config.wallet, "Wallet address")} className="p-2.5 bg-[#121212] text-white hover:bg-[#4A5D23] transition-colors duration-150" aria-label="Copy wallet"><Copy size={14} /></button>
          </div>
        </div>
        <p className="text-[11px] text-[#8B0000] font-semibold">⚠ Send native USDC on Base only. Funds sent on other networks (Ethereum, Polygon) or other tokens cannot be recovered.</p>
        <div className="border-t border-black/10 pt-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-black/50 mb-1">Transaction Hash</p>
          <div className="flex gap-2">
            <input data-testid="usdc-txhash-input" value={txHash} onChange={(e) => setTxHash(e.target.value)} placeholder="0x…"
              className="flex-1 border border-black/20 px-3 py-2.5 text-sm bg-white font-mono focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
            <button data-testid="usdc-verify-btn" type="button" onClick={verify} disabled={verifying}
              className="bg-[#4A5D23] text-white px-5 text-xs font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150 disabled:opacity-50">
              {verifying ? "Verifying…" : "Verify Payment"}
            </button>
          </div>
          <p className="text-[11px] text-black/40 mt-2">Your wallet shows the transaction hash after sending. Verification usually takes a few seconds.</p>
        </div>
      </div>
    </div>
  );
}

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();
  const polls = useRef(0);
  const sessionId = params.get("session_id");
  const accessToken = params.get("t");

  const load = useCallback(() => api.get(`/orders/${orderId}${accessToken ? `?t=${accessToken}` : ""}`).then(({ data }) => { setOrder(data); return data; }).catch(() => setOrder(false)), [orderId, accessToken]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!order || order === false) return;
    if (order.payment_status === "paid") {
      if (sessionId) { clearCart(); sessionStorage.removeItem("shodan_coupon"); }
      return;
    }
    if (!sessionId || polls.current > 20) return;
    const t = setTimeout(async () => {
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") await load();
        else setOrder({ ...order });
      } catch { setOrder({ ...order }); }
    }, 2500);
    return () => clearTimeout(t);
  }, [order, sessionId, load, clearCart]);

  if (order === false) return (
    <div className="max-w-3xl mx-auto px-4 py-32 text-center text-black/50">
      Order not found or you don't have access to it.
      <p className="mt-4"><Link to="/track" className="text-[#4A5D23] font-bold underline">Track your order with your email instead →</Link></p>
    </div>
  );
  if (!order) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-black/40 animate-pulse">Loading order…</div>;

  const pending = order.payment_status !== "paid";
  const isUsdc = order.payment_method === "usdc";
  const methodLabel = isUsdc ? "USDC on Base" : order.payment_method === "stripe" ? "Card (Stripe)" : "Card";

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-16">
      <div className="text-center mb-10">
        {pending ? <Clock size={56} className="text-[#B8860B] mx-auto animate-pulse" /> : <CheckCircle2 size={56} className="text-[#4A5D23] mx-auto" />}
        <h1 data-testid="order-confirmed-title" className="font-heading text-3xl md:text-4xl uppercase font-bold tracking-tight mt-4">{pending ? (isUsdc ? "Awaiting USDC Payment" : "Processing Payment…") : "Order Confirmed"}</h1>
        <p className="text-black/60 mt-2 text-sm">{pending ? (isUsdc ? "Complete the USDC transfer below to confirm your order." : "Confirming your payment with Stripe — this usually takes a few seconds.") : `Thanks, ${order.name}. A confirmation was sent to ${order.email}.`}</p>
        <p data-testid="order-id" className="font-mono text-sm font-bold mt-2 tracking-wider">{order.order_id}</p>
      </div>
      {pending && isUsdc && <UsdcPaymentPanel order={order} onPaid={() => { clearCart(); sessionStorage.removeItem("shodan_coupon"); load(); }} />}
      <div className="border border-black/10">
        <div className="bg-[#F4F3F0] px-6 py-3 flex justify-between text-xs font-bold uppercase tracking-widest">
          <span>Payment: {methodLabel}</span>
          <span data-testid="order-payment-status" className={pending ? "text-[#B8860B]" : "text-[#2E8B57]"}>Status: {pending ? "Pending" : "Paid"}</span>
        </div>
        <div className="p-6 space-y-4">
          {order.items.map((i) => (
            <div key={`${i.item_type}-${i.slug}-${i.sku_code || ""}`} className="flex items-center gap-4 text-sm">
              <img src={resolveImg(i.image)} alt={i.name} className="w-14 h-14 object-cover border border-black/5" />
              <div className="flex-1">
                <p className="font-semibold">{i.name}</p>
                {i.variant_label && i.item_type === "kit" && <p className="text-[11px] text-[#4A5D23]">{i.variant_label}</p>}
                <p className="text-xs text-black/50">Qty {i.quantity}</p>
              </div>
              <span className="font-bold">{money(i.price * i.quantity)}</span>
            </div>
          ))}
          <div className="border-t border-black/10 pt-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-black/60">Subtotal</span><span>{money(order.subtotal)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-[#4A5D23]"><span>Discount ({order.coupon_code})</span><span>-{money(order.discount)}</span></div>}
            {order.member_discount > 0 && <div className="flex justify-between text-[#4A5D23]"><span>Club Member (5%)</span><span>-{money(order.member_discount)}</span></div>}
            {order.crypto_discount > 0 && <div className="flex justify-between text-[#4A5D23]"><span>Crypto Discount (2%)</span><span>-{money(order.crypto_discount)}</span></div>}
            <div className="flex justify-between"><span className="text-black/60">Shipping</span><span>{order.shipping === 0 ? "FREE" : money(order.shipping)}</span></div>
            <div className="flex justify-between font-bold text-lg pt-2"><span>Total</span><span data-testid="order-total">{money(order.total)}</span></div>
          </div>
          <p className="text-[11px] text-black/40 font-mono flex items-center gap-2">Transaction ID: {order.payment_transaction_id || "—"}
            {isUsdc && order.payment_transaction_id && <a data-testid="basescan-link" href={`https://basescan.org/tx/${order.payment_transaction_id}`} target="_blank" rel="noreferrer" className="text-[#4A5D23] font-bold inline-flex items-center gap-1">BaseScan <ExternalLink size={11} /></a>}
          </p>
        </div>
      </div>
      <div className="text-center mt-10 flex flex-wrap justify-center gap-4">
        <Link data-testid="continue-shopping-btn" to="/shop" className="bg-[#121212] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">Continue Shopping</Link>
        <Link data-testid="track-order-link" to={`/track?order_id=${order.order_id}&email=${encodeURIComponent(order.email)}`} className="border border-black px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-150">Track This Order</Link>
      </div>
    </div>
  );
}
